import { Auth } from "aws-amplify";
import {
  FuseAccountingCentralDatastore,
  ListFixedFeeConfigsCommand,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";
import { getFacdConfig } from "./fuse-accounting-central-datastore-config";
import { RegionalDomain } from "./BartLambdaConfig";
import { isProd } from "src/util/is-prod";

const PROD_DOMAINS: RegionalDomain[] = ["prod-US", "prod-EU", "prod-FE"];
const DEVO_DOMAINS: RegionalDomain[] = ["devo-FE"];

function getDomains(): RegionalDomain[] {
  return isProd() ? PROD_DOMAINS : DEVO_DOMAINS;
}

export interface RegionConfig {
  config: SimplifiedFixedFeeConfig;
  region: RegionalDomain;
}

export type RegionAwareConfig = SimplifiedFixedFeeConfig & {
  region: RegionalDomain;
};

/**
 * Creates a single FACD client for a given domain using current Cognito credentials.
 * Shared factory used by both batch (createAllRegionClients) and lazy (JemGen context) patterns.
 */
export async function createFacdClient(
  domain: RegionalDomain,
): Promise<FuseAccountingCentralDatastore> {
  const credentials = Auth.essentialCredentials(
    await Auth.currentCredentials(),
  );
  return new FuseAccountingCentralDatastore({
    ...getFacdConfig(domain),
    credentials,
  });
}

/**
 * Creates FACD clients for all regions using current Cognito credentials.
 * In prod: queries all 3 regions. In beta: queries devo-FE only.
 */
export async function createAllRegionClients(): Promise<
  Record<RegionalDomain, FuseAccountingCentralDatastore>
> {
  const domains = getDomains();
  const entries = await Promise.all(
    domains.map(
      async (domain) => [domain, await createFacdClient(domain)] as const,
    ),
  );
  return Object.fromEntries(entries) as Record<
    RegionalDomain,
    FuseAccountingCentralDatastore
  >;
}

/**
 * Lists all FixedFeeConfigs across all regions in parallel.
 * Each result is tagged with its source region.
 * Uses Promise.allSettled to allow partial results if a region fails.
 */
export async function listAllRegionConfigs(
  existingClients?: Record<string, FuseAccountingCentralDatastore>,
): Promise<RegionConfig[]> {
  const clients = existingClients ?? (await createAllRegionClients());
  const domains = getDomains();
  const results = await Promise.allSettled(
    domains.map(async (domain) => {
      const resp = await clients[domain].send(
        new ListFixedFeeConfigsCommand({}),
      );
      return (resp.fixedFeeConfigs ?? []).map((config) => ({
        config,
        region: domain,
      }));
    }),
  );
  const configs: RegionConfig[] = [];
  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      configs.push(...result.value);
    } else {
      console.error(
        `Error fetching configs from ${domains[index]}:`,
        result.reason,
      );
    }
  });
  return configs;
}
